import levelService from '~/services/level.service';
import { gameSelectors, levelSelectors } from '~/store';
// ---- internal imports ----

import { LEVEL_LOAD_GAME } from '../actionTypes';
import { DEFAULT_STARTING_DATE, TURN_PHASES } from '~/lib/constants';
// ---- parent imports ----

import setUpFirstLevelForNewGame from './setUpFirstLevelForNewGame';
import goToNewLevel from './goToNewLevel';
import levelFailed from './levelFailed';
// ---- local imports ----


function loadLevel(gameUser, levelData = null, checkpointReloadIndex = null) {
	return async (dispatch, getState) => {
		if (levelData === null) {
			const gameUserId = gameSelectors.getGameId(getState());
			const result = await levelService.loadLevelData(gameUserId);

			if (!result.isSuccess) {
				return;
			}

			levelData = result.data;
		}

		if (levelData.level === null) {
			throw new Error('loadLevel failed, level is null');
		}
		else if (levelData.missions === null) {
			throw new Error('loadLevel failed, missions is null');
		}
		else if (levelData.level === 0) {
			dispatch(setUpFirstLevelForNewGame());
			return;
		}

		const level = levelData.level;
		const missions = levelData.missions;

		const levelMetadata = levelSelectors.getLevelData(getState(), level - 1);


		dispatch({
			type: LEVEL_LOAD_GAME,
			level,
			missions,
			story: levelMetadata.story,
			featuresToUnlock: levelMetadata.featuresToUnlock,
			isFirstAttemptInLevel: checkpointReloadIndex === 1,
		});

		const levelStatus = checkIfLevelAlreadyPassed(gameUser, missions, level);
		if (levelStatus === 'FAIL') {
			dispatch(levelFailed());
		}
		if (levelStatus === 'PASS') {
			dispatch(goToNewLevel(level + 1));
		}
	};
}

function checkIfLevelAlreadyPassed(gameUser, missions, level) {
	//	i will come here if
	// 	1. loading game, either after refresh 
	//	or after network request failure

	//	we need to handle the case where we have 
	//	a failed network request for going to next level 
	//	so we need to manually go to new level.

	//	if the level fails, then he will not reach here
	const turnNum = gameUser.turns;
	const turnPhase = gameUser.turn_phase;
	const missionStatus = missions.map((mission) => {
		const { startedInTurn, durationInTurns } = mission;
		const minimumMissionDuration = 3;
		const durationInTurnsIfDefined = durationInTurns ? durationInTurns : 0;
		const minimumLevelUpTurnNumber = Math.max(startedInTurn + minimumMissionDuration, startedInTurn + durationInTurnsIfDefined);

		if (turnNum < minimumLevelUpTurnNumber) {
			return 'UNASSESSED';
		}

		if (turnPhase === TURN_PHASES.CUSTOMERS) {
			return 'UNASSESSED';
		}

		const isDurationBased = durationInTurns ?? false;

		if (isDurationBased) {
			const hasDurationEnded = turnNum - startedInTurn === durationInTurns;

			if (!hasDurationEnded) {
				return 'UNASSESSED';
			}

			return  mission?.completed ? 'PASS': 'FAIL';
		} else {
			return mission?.completed ? 'PASS' : checkMissionCompletion(gameUser, mission);
		}
	});

	if (missionStatus.every((status) => { return status === 'PASS'})) {
		return 'PASS';
	}

	if (missionStatus.includes('FAIL')) {
		return 'FAIL';
	}

	if (missionStatus.includes('UNASSESSED')) {
		return 'UNASSESSED';
	}

}

function checkMissionCompletion(gameUser, mission) {
	const currentQuarterNum = dateToQuarterNum(gameUser.turn_date);
	const prevQuarterNum = currentQuarterNum - 1;

	const { level, financial_statements: financialStatements } = gameUser;

	if (level == 3) {
		const hasSubmittedFinancialStatements = financialStatements?.isSubmittedByQuarterNum[prevQuarterNum] ?? false;
		const latestFinancialStatements = financialStatements.systemStatementsByQuarterNum[prevQuarterNum];

		if (!hasSubmittedFinancialStatements) {
			return 'UNASSESSED';
		}
		if (latestFinancialStatements.balanceSheet.profit >= mission.target) {
			return 'PASS';
		} else {
			return 'FAIL';
		}
	}

	if (level == 4) {
		const hasSubmittedFinancialStatements = financialStatements?.isSubmittedByQuarterNum[prevQuarterNum] ?? false;

		if (!hasSubmittedFinancialStatements) {
			return 'UNASSESSED';
		}
		const latestFinancialStatements = financialStatements.systemStatementsByQuarterNum[prevQuarterNum];
		if (latestFinancialStatements.balanceSheet.profit >= mission.target) {
			return 'PASS';
		} else {
			return 'FAIL';
		}
	}

	if (level == 5) {

		function hasDistributedProfitForCurrentMonth(distributedProfits, turnDate) {
			const currentDate = new Date(turnDate);
		
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth() + 1;
		
			if (distributedProfits[year] && distributedProfits[year][month]) {
				return true;
			}
		
			return false;
		}

		const turnDate = gameUser.turn_date;
		const distributedProfits = financialStatements.distributedProfits;

		if (!hasDistributedProfitForCurrentMonth(distributedProfits, turnDate)) {
			return 'UNASSESSED';
		}

		const currentRetainedEarning = financialStatements?.currentRetainedEarnings;
		const latestFinancialStatements = financialStatements.systemStatementsByQuarterNum[prevQuarterNum];
		const { retainedEarnings, profit } = latestFinancialStatements.balanceSheet;

		const profitDistribution = (retainedEarnings + profit) - currentRetainedEarning;

		if (profitDistribution < mission.target) {
			return 'FAIL';
		}

		if (profitDistribution >= mission.target) {
			return 'PASS';
		}
	}

	if (level == 7) {
		const isFinancialRatiosSubmittedForLastQuarter  = financialStatements.isFinancialRatiosSubmittedForLastQuarter ?? false;

		if (!isFinancialRatiosSubmittedForLastQuarter) {
			return 'UNASSESSED';
		}

		const { netProfit,  revenue } = financialStatements?.systemStatementsByQuarterNum[prevQuarterNum]?.financialRatios;

		if (netProfit && revenue && revenue > 0) {
			const shouldPassMission = Math.round(netProfit/revenue) >= mission.target;
			return shouldPassMission ? 'PASS' : 'FAIL';
		}

		return 'UNASSESSED';
	}

	if (level == 8) {
		const isFinancialRatiosSubmittedForLastQuarter  = financialStatements.isFinancialRatiosSubmittedForLastQuarter ?? false;

		if (!isFinancialRatiosSubmittedForLastQuarter) {
			return 'UNASSESSED';
		}

		const { assetTurnoverRatio,  inventoryTurnoverRatio } = financialStatements?.systemStatementsByQuarterNum[prevQuarterNum]?.financialRatios;
		const { prevAssetTurnoverRatio,  pervInventoryTurnoverRatio } = financialStatements?.systemStatementsByQuarterNum[prevQuarterNum]?.financialRatios;

		let curr, prev;
		if (mission.customHandler === 'IMPROVED_INVENTORY_TURNOVER_RATIO') {
			curr = +inventoryTurnoverRatio;
			prev = +pervInventoryTurnoverRatio;
			return Math.round((curr - prev) / prev) >= mission.target ? 'PASS' : 'FAIL';
		}

		if (mission.customHandler === 'IMPROVED_ASSET_TURNOVER_RATIO') {
			curr = +assetTurnoverRatio;
			prev = +prevAssetTurnoverRatio;
			return Math.round((curr - prev) / prev) >= mission.target ? 'PASS' : 'FAIL';
		}

	}

}

function dateToQuarterNum(turnDate) {
	const currentTurnDate = new Date(turnDate);
	const quarterOfCurrentMonth = Math.ceil((currentTurnDate.getMonth() + 1) / 3);
	const numberOfYears = currentTurnDate.getFullYear() - DEFAULT_STARTING_DATE.getFullYear();
	const numberOfQuartersOfPreviousYears = 4 * numberOfYears;

	return quarterOfCurrentMonth + numberOfQuartersOfPreviousYears;
}

export default loadLevel;
